import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmationModel from "../../../components/common/ConfirmationModel";
import {
  DatePicker,
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
  SelectWithLabel,
} from "../../../components/micro/input";
import Breadcrumb from "../../../components/common/BreadCrumb";
import dayjs from "dayjs";
import { Eye, EyeSlash } from "../../../assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { createStaff, getOutlet } from "../../../redux/companies/slice";
import { IconButton } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import moment from "moment";

const AddEditStaff = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = location?.state?.mode;
  const staffData = location?.state?.staff;
  const storedData = sessionStorage.getItem("companyData");
  const companyData = storedData ? JSON.parse(storedData)?.company : null;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [passwordType, setPasswordType] = useState("password");
  const [confirmPasswordType, setConfirmPasswordType] = useState("password");

  // Selector
  const { outletList } = useSelector((store) => ({
    outletList: store?.companiesData?.getOutletStatus?.data,
  }));

  useEffect(() => {
    dispatch(
      getOutlet({
        search: "",
        pageCount: "",
        pageLimit: "all",
        company_id: companyData?.id || staffData?.company_id || "",
        sort_by: "name",
        sort_direction: "asc",
      })
    );
  }, []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Please enter name"),
    dateOfBirth: Yup.date().required("Please enter date of birth"),
    mobileNumber: Yup.string().trim().required("Please enter mobile number"),
    email: Yup.string().email("Invalid email").required("Please enter email"),
    password: Yup.string().required("Please enter password"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Please enter confirm password"),
    outletType: Yup.string().required("Please enter outlet's type"),
    outletRole: Yup.string().required("Please enter staff's role"),
  });

  const handleSubmit = async (values) => {
    setIsLoading(true);
    const payload = {
      name: values.name,
      dob: dayjs(values.dateOfBirth).format("DD-MM-YYYY"),
      mobile_number: values.mobileNumber,
      email: values.email,
      password: values.password,
      confirm_password: values.confirmPassword,
      company_id: staffData?.company_id || companyData?.id,
      outlet_id: values.outlet,
      outlet_type: values.outletType,
      role: values.outletRole,
    };

    const action = staffData?.id
      ? createStaff({ id: staffData.id, ...payload })
      : createStaff(payload);
    setIsLoading(true);
    try {
      const res = await dispatch(action);
      if (res.type === "createStaff/fulfilled") {
        navigate(-1);
      }
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  return (
    <div>
      <div className="mb-5">
        <h6 className="font-Montserrat font-normal text-xl mb-3">
          Company & Outlets
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={"Add Staff"}
          breadCrumbParent2={"Company & Outlets"}
          breadCrumbTitleKey={"/"}
          classes=""
        />
      </div>
      <div className="bg-white p-8 rounded-lg shadow-md w-full">
        <h1 className="text-base font-normal mb-1 font-Proxima">
          {companyData?.name}
        </h1>
        <h2 className="text-xl font-normal mb-4 font-Montserrat">
          {isEditMode === "edit" ? "Edit Staff" : "Add New Staff"}
        </h2>

        <Formik
          initialValues={{
            name: staffData?.user?.full_name || "",
            dateOfBirth: staffData?.user?.dob || "",
            mobileNumber: staffData?.user?.phone || "",
            email: staffData?.user?.email || "",
            password: "",
            confirmPassword: "",
            outlet: staffData?.outlet?.id || "",
            outletType: staffData?.outlet_type || "",
            outletRole: staffData?.user?.role || "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setFormValues(values);
            handleEditClick();
            setShowConfirmationModal(true);
          }}
        >
          {({
            errors,
            touched,
            handleBlur,
            handleChange,
            values,
            setFieldValue,
          }) => (
            <Form>
              <div className="mb-6">
                <h3 className="text-sm font-normal mb-3 font-Proxima">
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <InputWithLabel
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    }  ${
                      errors.name && touched.name
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    idFromName="name"
                    label="Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    errors={errors.name && touched.name}
                    errorsText={errors.name}
                    disabled={isEditMode == "edit" && !isEditing}
                  />
                  <div>
                    <DatePicker
                      value={
                        values.dateOfBirth ? moment(values.dateOfBirth) : null
                      }
                      onChange={(date) =>
                        setFieldValue(
                          "dateOfBirth",
                          date ? date.toDate() : null
                        )
                      }
                      label={"Select Birth Date"}
                      disabled={isEditMode == "edit" && !isEditing}
                      format="DD/MM/YYYY"
                      maxDate={moment()}
                      error={errors.dateOfBirth}
                      icon={null}
                      touched={touched.dateOfBirth}
                    />
                  </div>
                  <InputWithLabel
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    }  ${
                      errors.mobileNumber && touched.mobileNumber
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    idFromName="mobileNumber"
                    label="Mobile Number*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.mobileNumber}
                    errors={errors.mobileNumber && touched.mobileNumber}
                    errorsText={errors.mobileNumber}
                    disabled={isEditMode == "edit" && !isEditing}
                  />
                  <InputWithLabel
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    }  ${
                      errors.email && touched.email
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    idFromName="email"
                    label="Email*"
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    errors={errors.email && touched.email}
                    errorsText={errors.email}
                    disabled={isEditMode == "edit" && !isEditing}
                  />
                  <div>
                    <InputWithLabel
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black border hover:border-themeBtn"
                      }  ${
                        errors.password && touched.password
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out`}
                      idFromName="password"
                      label="Password"
                      type={passwordType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      errors={errors.password && touched.password}
                      errorsText={errors.password}
                      disabled={isEditMode == "edit" && !isEditing}
                      icon={
                        <p
                          className="p-2 absolute top-0 right-0 rounded bg-transparent"
                          onClick={() => {
                            if (passwordType === "password") {
                              setPasswordType("text");
                              return;
                            }
                            setPasswordType("password");
                          }}
                        >
                          {passwordType === "password" ? <EyeSlash /> : <Eye />}
                        </p>
                      }
                    />
                  </div>
                  <div>
                    <InputWithLabel
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black border hover:border-themeBtn"
                      }  ${
                        errors.confirmPassword && touched.confirmPassword
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out`}
                      idFromName="confirmPassword"
                      label="Confirm Password"
                      type={confirmPasswordType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.confirmPassword}
                      errors={errors.confirmPassword && touched.confirmPassword}
                      errorsText={errors.confirmPassword}
                      disabled={isEditMode == "edit" && !isEditing}
                      icon={
                        <p
                          className="p-2 absolute top-0 right-0 rounded bg-transparent"
                          onClick={() => {
                            if (confirmPasswordType === "password") {
                              setConfirmPasswordType("text");
                              return;
                            }
                            setConfirmPasswordType("password");
                          }}
                        >
                          {confirmPasswordType === "password" ? (
                            <EyeSlash />
                          ) : (
                            <Eye />
                          )}
                        </p>
                      }
                    />
                  </div>
                  <div>
                    <SelectWithLabel
                      idFromName="outletType"
                      placeholder="HQ/Outlet"
                      options={[
                        {
                          value: "Outlet",
                          label: "Outlet",
                        },
                        {
                          value: "HQ",
                          label: "HQ",
                        },
                      ]}
                      value={values?.outletType || ""}
                      setFieldValue={setFieldValue}
                      errors={errors.outletType}
                      touched={touched.outletType}
                      disabled={isEditMode == "edit" && !isEditing}
                      endAdornment={
                        values?.outletType ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue(`outletType`, "")}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        setFieldValue("outletType", event.target.value);
                      }}
                    />
                  </div>
                  <div>
                    <SelectWithLabel
                      idFromName="outletRole"
                      placeholder="Staff's Role"
                      options={[
                        {
                          value: "staff",
                          label: "Staff",
                        },
                        {
                          value: "manager",
                          label: "Manager",
                        },
                        {
                          value: "finance",
                          label: "Finance",
                        },
                        {
                          value: "operation",
                          label: "Operation",
                        },
                      ]}
                      value={values?.outletRole || ""}
                      setFieldValue={setFieldValue}
                      errors={errors.outletRole}
                      touched={touched.outletRole}
                      disabled={isEditMode == "edit" && !isEditing}
                      endAdornment={
                        values?.outletRole ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue(`outletRole`, "")}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        setFieldValue("outletRole", event.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-normal mb-3 font-Proxima">
                  Outlet Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <SelectWithLabel
                      idFromName="outlet"
                      placeholder="Outlet"
                      options={outletList?.data?.map((outlet) => ({
                        value: outlet.id,
                        label: outlet.name,
                      }))}
                      value={values?.outlet || ""}
                      setFieldValue={setFieldValue}
                      errors={errors.outletType}
                      touched={touched.outletType}
                      disabled={isEditMode == "edit" && !isEditing}
                      endAdornment={
                        values?.outlet ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue(`outlet`, "")}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        setFieldValue("outlet", event.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-3">
                <DefaultPrimaryButton
                  type="button"
                  value="Cancel"
                  onClick={() => navigate(-1)}
                />
                {isEditMode === "edit" ? (
                  isEditing ? (
                    <>
                      <DefaultButton type="submit" value="Save" />
                    </>
                  ) : (
                    <DefaultButton
                      value="Edit"
                      onClick={handleEditClick}
                      type="submit"
                    />
                  )
                ) : (
                  <DefaultButton type="submit" value="Save" />
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {showConfirmationModal && (
        <ConfirmationModel
          confirmText={isEditMode === "edit" ? "Edit" : "Create"}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={() => handleSubmit(formValues)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default AddEditStaff;
